// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
// -------------------------------------------------------------------------------
// Component and others
// -------------------------------------------------------------------------------
// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import StyledPaymentIntentCard, { StyledModal as Modal } from "./styles";
import { Button } from "components/Common/Button";
import { CheckIcon } from "components/Icons";
import { paymentPlans } from "helpers/constants";
import {
	useGetDomainGymQuery,
	useCancelSubscriptionMutation,
	useUnscheduleSubscriptionCancellationMutation,
} from "store/api/api";
import { useRecoilValue } from "recoil";
import { userAtom } from "globalAtoms";
import dayjs from "dayjs";
import usePrimaryColor from "helpers/hooks/usePrimaryColor";
import { WarningOutlined } from "@ant-design/icons";
import { Typography, Flex } from "antd";
// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
const PaymentIntentCard = React.memo(
	({ icon, className, plan, freeSubs, planDetails, pricing, subscription, ...other }) => {
		const { gym } = useParams();
		const navigate = useNavigate();
		const user = useRecoilValue(userAtom);
		const domainGymResponse = useGetDomainGymQuery(gym);
		const domainGymId = domainGymResponse?.data?.id;
		const [cancelSubscriptionTrigger] = useCancelSubscriptionMutation();
		const [unscheduleSubscriptionCancellationTrigger] = useUnscheduleSubscriptionCancellationMutation();
		const [confirmOpen, setConfirmOpen] = React.useState(false);
		const features = paymentPlans[planDetails.title.replace(/\s+/g, "").toLowerCase()].featuresList;
		const productName = paymentPlans[planDetails.title.replace(/\s+/g, "").toLowerCase()].productName;
		const primaryColor = usePrimaryColor();
		const handleCancelSubscription = async () => {
			if (subscription?.id && user?.customer_id && domainGymId) {
				await cancelSubscriptionTrigger({
					subscription_id: subscription?.id,
					customer_id: user?.customer_id,
					gym_id: domainGymId,
				});
				window.location.reload();
			}
		};
		const handleResubscribeSubscription = async () => {
			if (subscription?.id && user?.customer_id && domainGymId) {
				await unscheduleSubscriptionCancellationTrigger({
					subscription_id: subscription?.id,
					customer_id: user?.customer_id,
					gym_id: domainGymId,
				});
				window.location.reload();
			}
		};

		return (
			<>
				<Modal
					className="confirm-modal"
					open={confirmOpen}
					onCancel={() => setConfirmOpen(false)}
					footer={null}
					width={540}
					maskClosable={false}
					destroyOnClose={false}
				>
					<WarningOutlined style={{ fontSize: 32, color: "#D44215" }} />
					<Typography.Title style={{ margin: 0 }} level={3}>
						Cancel Subscription
					</Typography.Title>
					<Typography.Paragraph style={{ marginBottom: 24 }} align="center">
						Are you sure you want to cancel your subscription?
					</Typography.Paragraph>
					<Flex className="sb-confirm-form__actions" gap={16}>
						<Button
							className="primary-outlined"
							style={{ borderColor: primaryColor, color: primaryColor }}
							onClick={() => setConfirmOpen(false)}
						>
							<span style={{ color: primaryColor }}>No</span>
						</Button>
						<Button
							onClick={handleCancelSubscription}
							type="primary"
							className="primary-filled"
							style={{ backgroundColor: primaryColor }}
						>
							Confirm
						</Button>
					</Flex>
				</Modal>
				<StyledPaymentIntentCard className={`${className} sb-payment-intent-card`} {...other}>
					<div className="sb-payment-intent-card__plan">
						<div className="sb-payment-intent-card__plan__left">
							<div className="sb-payment-intent-card__plan__left__icon">{icon}</div>
							<div className="sb-payment-intent-card__plan__left__title">{plan}</div>
							<div className="sb-payment-intent-card__plan__left__subtitle">Up to {freeSubs} subscribers</div>
							<div className="sb-payment-intent-card__plan__left__disclaimer">*Overages apply</div>
							{subscription && subscription?.status === "active" && subscription?.canceled_at && (
								<div
									style={{
										color: "red",
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<p>Cancellation Date:</p>
									<p>{dayjs.unix(subscription.current_period_end).format("YYYY-MM-DD")}</p>
								</div>
							)}
							<Button
								className="primary-filled"
								onClick={() => navigate(`/${gym}/dashboard/select-plan`)}
								style={{ width: "50%", marginRight: "24px", marginLeft: "24px", marginBottom: "24px" }}
							>
								Change Plan
							</Button>
							{productName !== paymentPlans.starter.productName &&
								subscription &&
								subscription.status === "active" &&
								!subscription?.canceled_at && (
									<Button
										className="primary-filled"
										onClick={() => setConfirmOpen(true)}
										style={{
											width: "50%",
											marginRight: "24px",
											marginLeft: "24px",
											marginBottom: "24px",
											backgroundColor: primaryColor,
										}}
									>
										Cancel Subscription
									</Button>
								)}
							{subscription && subscription.status === "active" && subscription?.canceled_at && (
								<Button
									className="primary-filled"
									onClick={handleResubscribeSubscription}
									style={{
										width: "50%",
										marginRight: "24px",
										marginLeft: "24px",
										marginBottom: "24px",
										backgroundColor: "#27AE60",
									}}
								>
									Resubscribe
								</Button>
							)}
						</div>
						<div className="sb-payment-intent-card__inner">
							<div className="sb-payment-intent-card__inner__header">
								<div className="sb-payment-intent-card__inner__header__title">Plan Features</div>
								<div className="sb-payment-intent-card__inner__header__price">
									<div className="sb-payment-intent-card__inner__header__price">${pricing}</div>
									<div className="sb-payment-intent-card__inner__header__price__sub">/month</div>
								</div>
							</div>
							<div className="sb-payment-intent-card__inner__header__divider" />
							<div className="sb-payment-intent-card__inner__header__info">
								{features.map((item, index) => {
									return (
										<div key={index} className="sb-payment-intent-card__inner__header__info__row">
											<div className="sb-payment-intent-card__inner__header__info__row__title">
												<div
													style={{
														display: "flex",
														padding: 5,
														width: "20px",
														height: "20px",
														borderRadius: "50%",
														background: "rgba(255, 134, 92, .2)",
														alignItems: "center",
														justifyContent: "center",
														marginRight: "8px",
													}}
												>
													<CheckIcon style={{ color: "#FF865C" }} />
												</div>
												{item}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</StyledPaymentIntentCard>
			</>
		);
	}
);
export default PaymentIntentCard;

